import debounce                                   from 'lodash-es/debounce';
import * as React                                 from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import DV                                         from 'ts-data-validator';

import { useCommon }      from '../../../../providers/common';
import { useInformation } from '../../../../providers/information';

import InformationSection from '../section';
import InformationSidebar from '../sidebar';

const InformationContent: React.FC = function() {
  const history  = useHistory();
  const location = useLocation();
  const match    = useRouteMatch<Record<'page' | 'urlKey', string>>();

  const { pages }   = useCommon();
  const information = useInformation();

  const articles = React.useRef<HTMLElement | null>(null);
  const observer = React.useRef<IntersectionObserver | null>(null);

  const [activeCategory, setActiveCategory] = React.useState<number | undefined>(() => {
    let category;

    if (match.params.urlKey) {
      category = information.items.find(({ items }) => {
        return items.some((item) => {
          return item.urlKey === match.params.urlKey;
        });
      })?.id;
    }

    if (!category && information.items.length > 0) {
      const [{ id }] = information.items;

      category = id;
    }

    return category;
  });
  const [isMobile, setIsMobile]             = React.useState(false);
  const [expanded, setExpanded]             = React.useState(new Map<number, Map<number, Set<number>>>());
  const [isObservable, setIsObservable]     = React.useState(false);

  const page       = React.useMemo(() => {
    return (pages as any[]).find((page: any) => page.template === information.template);
  }, [pages, information.template]);
  const categories = React.useMemo(() => {
    let category = activeCategory;

    if (!category && information.items.length > 0) {
      if (match.params.urlKey) {
        category = information.items.find(({ items }) => {
          return items.some((item) => {
            return item.urlKey === match.params.urlKey;
          });
        })?.id;
      }

      if (!category && information.items.length > 0) {
        const [{ id }] = information.items;

        category = id;

        setActiveCategory(id);
      }
    }

    if (information.type === 'questions' && category && !isMobile) {
      return information.items.filter((item) => item.id === category);
    }

    return information.items;
  }, [activeCategory, information.items, isMobile, match.params.urlKey]);

  const get                  = React.useCallback((url: string) => {
    for (let category of categories) {
      for (let item of category.items) {
        if (item.urlKey === url) {
          return [category.id, item.id];
        }
      }
    }

    return [];
  }, [categories]);
  const handleResize         = React.useCallback(() => {
    const mobile = window.innerWidth < 1152;

    if (isMobile !== mobile) {
      setIsMobile(mobile);
    }
  }, [isMobile]);
  const isUrlExist           = React.useCallback((url: string) => {
    return categories.some(({ items }) => {
      return items.some(({ urlKey }) => {
        return urlKey === url;
      });
    });
  }, [categories, information.items, information.template]);
  const observe              = React.useCallback(() => {
    observer.current?.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (articles.current) {
        const visible = entries.filter(({ isIntersecting }) => isIntersecting);

        if (visible.length) {
          const containerTop = articles.current.getBoundingClientRect().top;
          const closest      = visible.reduce((prev, curr) => {
            const prevDist = Math.abs(prev.boundingClientRect.top - containerTop);
            const currDist = Math.abs(curr.boundingClientRect.top - containerTop);

            return currDist < prevDist ? curr : prev;
          });
          const target       = closest.target as HTMLElement;
          const alias        = target.dataset.alias as string;
          const url          = `/${page.urlKey}/${alias}`;

          if (location.pathname !== url && isUrlExist(alias)) {
            history.replace(url);
          }
        }
      }
    }, {
      root:       articles.current,
      threshold:  0,
      rootMargin: '0px 0px -100% 0px',
    });
  }, [articles, categories, isUrlExist, location.pathname, observer, page]);
  const toggleCollapse       = React.useCallback((category: number, item: number, index: number, force?: boolean) => {
    setExpanded((prev) => {
      const map      = new Map(prev.entries());
      const items    = map.get(category) ?? new Map();
      const sections = items.get(item) ?? new Set();

      if (force) {
        sections.add(index);
      } else if (sections.has(index)) {
        sections.delete(index);
      } else {
        sections.add(index);
      }

      items.set(item, sections);
      map.set(category, items);

      return map;
    });
  }, []);
  const scrollToArticle      = React.useCallback((url: string, smooth = true) => {
    const [category, item] = get(url);

    if (articles.current && category && item) {
      const target = articles.current.querySelector<HTMLElement>(`[data-alias='${url}']`);

      if (target) {
        setActiveCategory(category);
        toggleCollapse(category, item, 0, true);
        setIsObservable(false);

        articles.current.scrollTo({
          behavior: smooth ? 'smooth' : 'auto',
          top:      target.offsetTop,
        });

        history.replace(`/${page.urlKey}/${url}`);

        const observe = debounce(() => {
          setIsObservable(true);

          articles.current?.removeEventListener('scroll', observe);
        }, 150);

        articles.current.addEventListener('scroll', observe);
      }
    }
  }, [articles, categories, get, page.urlKey]);
  const handleCategoryChange = React.useCallback((categoryId: number) => {
    setActiveCategory(categoryId);
    setIsObservable(false);

    history.replace(`/${page.urlKey}`);

    articles.current?.scrollTo({
      behavior: 'auto',
      top:      0,
    });

    observe();

    articles.current?.addEventListener('scroll', () => {
      setIsObservable(true);
    }, { once: true });
  }, [articles, observe, page.urlKey]);

  React.useEffect(() => {
    scrollTo({
      left: 0,
      top:  0,
    });
    handleResize();

    window.addEventListener('orientationchange', handleResize);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('orientationchange', handleResize);
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  React.useEffect(() => {
    if (DV.isString(match.params.urlKey)) {
      scrollToArticle(match.params.urlKey);
    } else {
      articles.current?.scrollTo({
        left: 0,
        top:  0,
      });

      articles.current?.addEventListener('scroll', () => {
        setIsObservable(true);
      }, { once: true });
    }
  }, [information.isLoaded]);
  React.useEffect(() => {
    if (!isObservable) {
      observer.current?.disconnect();
    }
  }, [isObservable]);
  React.useEffect(observe, [categories, information.items]);
  React.useEffect(() => {
    if (!information.isLoaded && !information.loading) {
      setActiveCategory(void 0);
      setExpanded(new Map());
      setIsObservable(false);

      void information.getInitialState(information.template, match.params.page)
        .finally(() => {
          articles.current?.addEventListener('scroll', () => {
            setIsObservable(true);
          }, { once: true });
        });
    }
  }, [information.template]);

  return (
    <main>
      <InformationSidebar
        activeCategory={activeCategory}
        handleCategoryChange={handleCategoryChange}
        scrollToArticle={scrollToArticle}
      />
      <section
        className={'articles'}
        itemScope
        itemType={'http://schema.org/FAQPage'}
        ref={articles}
      >
        <InformationSection
          categories={categories}
          expandedEntries={expanded}
          isMobile={isMobile}
          observer={observer}
          isObservable={isObservable}
          toggleCollapse={toggleCollapse}
        />
      </section>
    </main>
  );
};

export default InformationContent;